import React from 'react';
import { motion } from 'framer-motion';
import { 
  Target, 
  Eye, 
  Heart, 
  Users, 
  Award, 
  Globe,
  Lightbulb,
  Handshake
} from 'lucide-react';
import { Container, Section, Badge, Card } from '../components/ui';
import { CTA, PageHero, OurPurpose } from '../components/sections';

const values = [
  {
    icon: Lightbulb,
    title: 'Innovation',
    description: 'We constantly explore emerging technologies and creative solutions to solve complex challenges.',
  },
  {
    icon: Award,
    title: 'Excellence',
    description: 'We hold ourselves to the highest standards in everything we deliver, from code quality to client communication.',
  },
  {
    icon: Handshake,
    title: 'Partnership',
    description: "We build lasting relationships with our clients, treating their success as our own.",
  },
  {
    icon: Heart,
    title: 'Integrity',
    description: 'We operate with transparency, honesty, and ethical practices in all our business dealings.',
  },
];

const milestones = [
  { year: '2014', title: 'Founded', description: 'ISTATEC was established with a vision to transform businesses through technology.' },
  { year: '2016', title: 'First Major Client', description: 'Secured our first enterprise client, delivering a complete digital transformation.' },
  { year: '2019', title: 'Global Expansion', description: 'Expanded operations to serve clients across North America, Europe, and Asia.' },
  { year: '2022', title: 'AI Pioneer', description: 'Launched our GenAI practice, helping businesses leverage artificial intelligence.' },
  { year: '2024', title: 'FinExtract Launch', description: 'Released our flagship AI product for financial document processing.' },
];

export const About: React.FC = () => {
  return (
    <>
      {/* Hero Section */}
      <PageHero
        badge={
          <Badge className="mb-6 bg-white/10 text-white border border-white/20">
            About ISTATEC
          </Badge>
        }
        title="Empowering Businesses Through Technology Excellence"
        subtitle="We are a team of passionate technologists dedicated to helping businesses navigate digital transformation and achieve their full potential."
      />

      {/* Our Purpose */}
      <OurPurpose />

      {/* Mission & Vision */}
      <Section background="white" padding="lg">
        <Container>
          <div className="grid md:grid-cols-2 gap-12">
            <motion.div
              initial={{ opacity: 0, x: -20 }}
              whileInView={{ opacity: 1, x: 0 }}
              viewport={{ once: true }}
            >
              <Card className="h-full border-l-4 border-l-accent-500">
                <div className="flex items-center gap-4 mb-4">
                  <div className="w-12 h-12 rounded-xl bg-accent-100 flex items-center justify-center">
                    <Target className="w-6 h-6 text-accent-600" />
                  </div>
                  <h2 className="text-2xl font-bold text-navy-900">Our Mission</h2>
                </div>
                <p className="text-navy-600 leading-relaxed">
                  To empower businesses with cutting-edge technology solutions and expert talent, 
                  enabling them to innovate faster, operate more efficiently, and achieve 
                  sustainable growth in the digital economy.
                </p>
              </Card>
            </motion.div>

            <motion.div
              initial={{ opacity: 0, x: 20 }}
              whileInView={{ opacity: 1, x: 0 }}
              viewport={{ once: true }}
              transition={{ delay: 0.2 }}
            >
              <Card className="h-full border-l-4 border-l-navy-900">
                <div className="flex items-center gap-4 mb-4">
                  <div className="w-12 h-12 rounded-xl bg-navy-100 flex items-center justify-center">
                    <Eye className="w-6 h-6 text-navy-700" />
                  </div>
                  <h2 className="text-2xl font-bold text-navy-900">Our Vision</h2>
                </div>
                <p className="text-navy-600 leading-relaxed">
                  To be the most trusted technology partner for enterprises worldwide, 
                  recognized for our expertise, innovation, and commitment to delivering 
                  transformative solutions that drive real business value.
                </p>
              </Card>
            </motion.div>
          </div>
        </Container>
      </Section>

      {/* Our Values */}
      <Section background="white" padding="lg">
        <Container>
          <div className="text-center max-w-2xl mx-auto mb-12">
            <Badge variant="accent" className="mb-4">Our Values</Badge>
            <h2 className="heading-lg text-navy-900 mb-4">What Drives Us</h2>
            <p className="text-body text-lg">
              Our core values guide every decision we make and every relationship we build.
            </p>
          </div>

          <div className="grid md:grid-cols-2 lg:grid-cols-4 gap-6">
            {values.map((value, index) => (
              <motion.div
                key={value.title}
                initial={{ opacity: 0, y: 20 }}
                whileInView={{ opacity: 1, y: 0 }}
                viewport={{ once: true }}
                transition={{ delay: index * 0.1 }}
              >
                <Card hover className="h-full text-center">
                  <div className="w-14 h-14 mx-auto mb-4 rounded-2xl bg-gradient-to-br from-navy-100 to-accent-50 flex items-center justify-center">
                    <value.icon className="w-7 h-7 text-navy-700" />
                  </div>
                  <h3 className="text-xl font-semibold text-navy-900 mb-2">{value.title}</h3>
                  <p className="text-navy-600 text-sm">{value.description}</p>
                </Card>
              </motion.div>
            ))}
          </div>
        </Container>
      </Section>

      {/* Timeline */}
      <Section background="gray" padding="lg">
        <Container size="md">
          <div className="text-center mb-12">
            <Badge variant="primary" className="mb-4">Our Journey</Badge>
            <h2 className="heading-lg text-navy-900">Growing Together</h2>
          </div>

          <div className="relative">
            {/* Timeline Line */}
            <div className="absolute left-1/2 transform -translate-x-1/2 h-full w-0.5 bg-navy-200" />

            {milestones.map((milestone, index) => (
              <motion.div
                key={milestone.year}
                className={`relative flex items-center mb-12 last:mb-0 ${
                  index % 2 === 0 ? 'flex-row' : 'flex-row-reverse'
                }`}
                initial={{ opacity: 0, x: index % 2 === 0 ? -50 : 50 }}
                whileInView={{ opacity: 1, x: 0 }}
                viewport={{ once: true }}
              >
                <div className={`w-1/2 ${index % 2 === 0 ? 'pr-12 text-right' : 'pl-12'}`}>
                  <Card className="inline-block">
                    <span className="text-accent-500 font-mono font-bold">{milestone.year}</span>
                    <h3 className="text-xl font-semibold text-navy-900 mt-1">{milestone.title}</h3>
                    <p className="text-navy-600 text-sm mt-2">{milestone.description}</p>
                  </Card>
                </div>
                
                {/* Timeline Dot */}
                <div className="absolute left-1/2 transform -translate-x-1/2 w-4 h-4 bg-accent-500 rounded-full border-4 border-white shadow-md" />
              </motion.div>
            ))}
          </div>
        </Container>
      </Section>

      {/* Global Presence */}
      <Section background="white" padding="lg">
        <Container>
          <div className="grid lg:grid-cols-2 gap-12 items-center">
            <div>
              <Badge variant="accent" className="mb-4">Global Reach</Badge>
              <h2 className="heading-lg text-navy-900 mb-6">
                Serving Clients Worldwide
              </h2>
              <p className="text-body text-lg mb-6">
                From startups to Fortune 500 companies, we partner with businesses 
                across the globe to deliver transformative technology solutions.
              </p>
              <div className="grid grid-cols-2 gap-4">
                <Card padding="sm" className="text-center">
                  <Globe className="w-8 h-8 text-accent-500 mx-auto mb-2" />
                  <div className="text-2xl font-bold text-navy-900">15+</div>
                  <div className="text-sm text-navy-600">Countries Served</div>
                </Card>
                <Card padding="sm" className="text-center">
                  <Users className="w-8 h-8 text-accent-500 mx-auto mb-2" />
                  <div className="text-2xl font-bold text-navy-900">200+</div>
                  <div className="text-sm text-navy-600">Happy Clients</div>
                </Card>
              </div>
            </div>
            
            <div className="bg-navy-900 rounded-3xl p-8 text-white">
              <h3 className="text-xl font-semibold mb-6">Our Locations</h3>
              <div className="space-y-4">
                {[
                  { region: 'North America', cities: 'San Francisco, New York, Toronto' },
                  { region: 'Europe', cities: 'London, Berlin, Amsterdam' },
                  { region: 'Asia Pacific', cities: 'Singapore, Sydney, Mumbai' },
                ].map((location) => (
                  <div key={location.region} className="flex items-start gap-3">
                    <div className="w-2 h-2 rounded-full bg-accent-400 mt-2" />
                    <div>
                      <div className="font-semibold">{location.region}</div>
                      <div className="text-navy-300 text-sm">{location.cities}</div>
                    </div>
                  </div>
                ))}
              </div>
            </div>
          </div>
        </Container>
      </Section>

      <CTA />
    </>
  );
};

